import { CautionIcon } from '@/components/Icons';
import { useGetSyncStatus } from '@/modules/Sync/queries';
import { DateUtility } from '@/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSyncDelay, getSyncDelay } from './sync-status.utils';
import { SyncWarningDialog } from './SyncWarningDialog';

export const SyncStatus = () => {
  const { t, i18n } = useTranslation();
  const {
    data: lastSuccessfulSyncAt,
    isPending,
    isError,
    isFetchedAfterMount,
    dataUpdatedAt,
  } = useGetSyncStatus();
  const [now, setNow] = useState(Date.now);
  const [dismissedSyncTimestamp, setDismissedSyncTimestamp] =
    useState<number>();

  useEffect(() => {
    const updateNow = () => setNow(Date.now());
    const interval = window.setInterval(updateNow, 60000);
    window.addEventListener('focus', updateNow);
    document.addEventListener('visibilitychange', updateNow);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateNow);
      document.removeEventListener('visibilitychange', updateNow);
    };
  }, []);

  const delay = getSyncDelay(
    lastSuccessfulSyncAt,
    Math.max(now, dataUpdatedAt),
  );
  const isStale = delay?.isStale ?? false;
  const duration = delay ? formatSyncDelay(delay.elapsedMs, i18n.language) : '';
  const syncTimestamp = lastSuccessfulSyncAt?.getTime();

  if (isPending) {
    return null;
  }

  const label =
    lastSuccessfulSyncAt && delay
      ? t('last_successful_sync', {
          date: DateUtility.formatDate(lastSuccessfulSyncAt, i18n.language, {
            monthFormat: '2-digit',
            yearFormat: 'numeric',
            hourFormat: '2-digit',
            minuteFormat: '2-digit',
            hour12: false,
          }),
          interpolation: { escapeValue: false },
        })
      : isError || lastSuccessfulSyncAt
        ? t('sync_status_unavailable')
        : t('sync_never_completed');

  return (
    <>
      <span
        className="inline-flex items-center gap-2 text-sm text-white"
        role="status"
      >
        {isStale && (
          <button
            type="button"
            className="shrink-0 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
            aria-label={t('sync_stale_warning_icon', { duration })}
            title={t('sync_stale_warning_icon', { duration })}
            onClick={() => setDismissedSyncTimestamp(undefined)}
          >
            <CautionIcon
              aria-hidden="true"
              className="size-5 [&_g]:fill-yellow"
            />
          </button>
        )}
        <span>{label}</span>
      </span>
      {isStale &&
        isFetchedAfterMount &&
        dismissedSyncTimestamp !== syncTimestamp && (
          <SyncWarningDialog
            duration={duration}
            lastSyncLabel={label}
            onClose={() => setDismissedSyncTimestamp(syncTimestamp)}
          />
        )}
    </>
  );
};
